import { FC, Fragment, useState } from 'react';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	Result,
	Row,
	Select,
	Table,
} from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import FleetTitle from '@/libs/antd/components/Title';
import {
	getFBDHRejectVisitReportDetail,
	submitFBDHApprovalRejection,
} from '@/libs/fleet-api/services/visit-reports';
import MainInformation from '@/components/MainInformation';
import FleetModal from '@/libs/antd/components/Modal';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import { useForm } from '@/libs/antd/form';
import { useApp } from '@/App';
import rule from '@/libs/antd/rules';
import { LoadingOutlined } from '@ant-design/icons';
import { ApprovalRejectVisitReport } from '@/libs/fleet-api/services/types';
import fetcher, { FleetFile } from '@/libs/fleet-api/fetcher';
import { useModals } from '@/components/modals/ModalProvider';

type FBDHVisitReportRejectPageProps = {
	prospectId: string;
};

type RejectVisitReportForm = ApprovalRejectVisitReport;

const FBDHVisitReportRejectPage: FC<FBDHVisitReportRejectPageProps> = ({
	prospectId,
}) => {
	const { navigate, notification } = useApp();
	const { data, isLoading, isFetching } = useQuery({
		queryKey: ['getFBDHRejectVisitReportDetail', prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			return getFBDHRejectVisitReportDetail(id);
		},
		enabled: !!prospectId,
	});
	const [showModal, setShowModal] = useState<boolean>(false);
	const { form } = useForm<RejectVisitReportForm>();

	const approvalRejectMutation = useMutation({
		mutationFn: submitFBDHApprovalRejection,
	});

	const { openPreviewModal } = useModals();

	const {
		mainInformation,
		surveyInformation,
		customerBusinessDocuments,
		customerLegalDocuments,
		customerReferralDocuments,
		approval,
		rejectInformation,
	} = data?.data ?? {};

	const handleModalOk = async () => {
		const isValid = await form.validateFields();
		if (isValid) {
			try {
				const payload = {
					...form?.getFieldsValue(),
				};
				await approvalRejectMutation.mutateAsync(payload);
				setShowModal(false);
				notification.success({
					message: 'Success',
					description: 'Approval Reject Laporan Kunjungan berhasil di simpan',
				});
				navigate('FBDHVisitReportRejectListPage');
			} catch (error) {
				if (fetcher.isError(error)) {
					const message = await error.json();
					notification.error({
						duration: 20,
						message: `Error ID: ${message?.metadata?.debugInfo?.errorId ?? 'Unknown'}`,
						description: (
							<div>
								{message?.metadata?.debugInfo?.messages &&
								message?.metadata?.debugInfo?.messages.length ? (
									<ul>
										{message?.metadata?.debugInfo?.messages.map(
											(msg: string) => <li>{msg}</li>,
										)}
									</ul>
								) : (
									<pre>Opps, Something went wrong</pre>
								)}
							</div>
						),
					});
				}
			}
		}
	};

	if (isFetching) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}

	return (
		<Fragment>
			<MainInformation
				mainInformation={mainInformation}
				surveyInformation={surveyInformation}
				customerBusinessDocuments={customerBusinessDocuments}
				customerLegalDocuments={customerLegalDocuments}
				customerReferralDocuments={customerReferralDocuments}
				isLoading={isLoading}
			/>

			<Divider />

			<Form.Provider>
				<Form
					{...ANTD_DEFAULT_PROPS.FORM}
					form={form}
					onSubmitCapture={async () => {
						const isFormValid = await form.validateFields();
						if (isFormValid) {
							setShowModal(true);
						}
					}}
				>
					<Form.Item
						name="surveyId"
						hidden
						initialValue={surveyInformation!.id}
						noStyle
					/>
					<FleetTitle>Reject Notes</FleetTitle>

					<Form.Item
						label="Category Reject"
						labelCol={{ span: 6 }}
						wrapperCol={{
							style: {
								color: 'blue',
								marginLeft: -10,
							},
						}}
					>
						<Input
							disabled
							value={rejectInformation?.rejectCategory.value}
						/>
					</Form.Item>
					<Divider />
					<Form.Item
						label="View Photo"
						labelCol={{ span: 6 }}
						wrapperCol={{
							style: {
								marginLeft: -10,
							},
						}}
					>
						<a
							className="trigger-popup"
							href="#"
							onClick={(e) => {
								e.preventDefault();
								openPreviewModal(rejectInformation?.photos as FleetFile);
							}}
						>
							{rejectInformation?.photos.name}
						</a>
					</Form.Item>
					<Divider />
					<Form.Item
						label="Reject Notes"
						labelCol={{ span: 6 }}
						wrapperCol={{
							style: {
								marginLeft: -10,
							},
						}}
					>
						<Input.TextArea
							rows={4}
							disabled
							value={rejectInformation?.rejectNotes}
						/>
					</Form.Item>
					<div style={{ marginBottom: 20 }} />

					{approval && approval.approvalHistories && (
						<Fragment>
							<FleetTitle>Approval History</FleetTitle>
							<Table
								bordered
								loading={isLoading}
								pagination={false}
								dataSource={
									approval.approvalHistories.map((approval, key) => {
										return {
											key,
											...approval,
										};
									}) ?? []
								}
								columns={[
									{
										title: 'No',
										render: (_, record) => record.no,
									},
									{
										title: 'Name',
										render: (_, record) => record.approvalBy,
									},
									{
										title: 'Date',
										render: (_, record) => record.approvalDate,
									},
									{
										title: 'Process',
										render: (_, record) => record.approvalProcess,
									},
									{
										title: 'Status',
										align: 'center',
										render: (_, record) => {
											return (
												<span
													className={`review-status ${record.approvalStatus.toLowerCase()}`}
												>
													{record.approvalStatus === 'RequestReject'
														? 'Request Reject'
														: record.approvalStatus}
												</span>
											);
										},
									},
									{
										title: 'Approval Notes',
										render: (_, record) => record.approvalNote,
									},
								]}
								style={{ marginBottom: 20 }}
							/>
						</Fragment>
					)}
					<Row gutter={[40, 20]}>
						<Col span={12}>
							<Form.Item
								name="approvalStatus"
								label="Approval Status"
								rules={[rule.required()]}
							>
								<Select
									allowClear={true}
									options={[{ value: 'Approve' }, { value: 'Return' }]}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Divider />
					<Form.Item
						name="notes"
						label="Approval Notes"
						labelCol={{ span: 6 }}
						wrapperCol={{
							style: {
								color: 'blue',
								marginLeft: -10,
							},
						}}
						rules={[rule.required()]}
					>
						<Input.TextArea rows={3} />
					</Form.Item>
					<Button
						type="primary"
						htmlType="submit"
						style={{
							width: 200,
							display: 'block',
							marginLeft: 'auto',
							marginTop: 20,
						}}
					>
						SAVE
					</Button>
				</Form>
			</Form.Provider>

			<FleetModal
				open={showModal}
				styles={{ footer: { padding: 0 } }}
				okText="NEXT"
				cancelText="CANCEL"
				onCancel={() => setShowModal(false)}
				onOk={handleModalOk}
			>
				<Result
					subTitle={
						<p style={{ color: '#2F2F2F' }}>
							Apakah Anda yakin ingin{' '}
							<b>
								{form.getFieldValue('approvalStatus')?.toUpperCase()} Request
								Reject
							</b>{' '}
							laporan kunjungan ini?
						</p>
					}
				></Result>
			</FleetModal>
		</Fragment>
	);
};

export default FBDHVisitReportRejectPage;
